<script setup>
import { ref, computed, watch } from 'vue'
import { store } from '@/store/appStore.js'
import { STAGE, LATERAL_STATES } from '@/data/shared/orderModel.js'

// Two gates in the revised flow:
//   1. MR raises, visits the pharmacy in person, and punches in the OTP
//      themselves — there's no separate pharmacist device in this flow,
//      the MR reads/is told the code and enters it on their own phone.
//      Single-device view.
//   2. MR's order -> Sales Supervisor approves, genuinely two different
//      people/devices, so that stays a side-by-side view.
const isSupervisor = computed(() => store.state.role === 'salesSupervisor')
const gate = ref(isSupervisor.value ? 'supervisor' : 'otp')
const otpInput = ref('')
const commentText = ref('')
const rejectReason = ref('')
const flash = ref(null)
const notify = (msg) => { flash.value = msg; setTimeout(() => { flash.value = null }, 2600) }

// The full shared pool, not the MR-scoped view — other execs' pre-seeded
// orders can sit at these same gates, and the Supervisor's queue in
// particular needs to see the whole team, not just Tendai Moyo's.
const orders = computed(() => store.salesSupervisorV2.orders.value)
const otpQueue = computed(() => orders.value.filter(o => o.currentStage === STAGE.OTP_CONFIRMATION))
const approvalQueue = computed(() => orders.value.filter(o => o.currentStage === STAGE.SUPERVISOR_APPROVAL))
const queue = computed(() => gate.value === 'otp' ? otpQueue.value : approvalQueue.value)

// selectedOrderNo is now the single source of truth for which order is on
// screen — no silent "fall back to queue[0]" inside the order computed.
// That fallback was the bug: with only one order in the queue (the common
// case, since no pill row even renders for a single item — see below),
// nothing ever explicitly set selectedOrderNo, so once the acted-on order
// left the queue the fallback had nothing left to return and the whole
// order and its outcome message vanished.
const selectedOrderNo = ref(null)
const order = computed(() => selectedOrderNo.value ? (orders.value.find(o => o.orderNo === selectedOrderNo.value) || null) : null)

// Pin a real selection the moment there's something to show — on load, on
// switching gates, or whenever the current selection stops existing (e.g.
// a fresh page load where nothing's picked yet). Never re-picks just
// because an already-selected order's stage changed.
watch([gate, queue], () => {
  if (selectedOrderNo.value && orders.value.some(o => o.orderNo === selectedOrderNo.value)) return
  selectedOrderNo.value = queue.value[0]?.orderNo || null
}, { immediate: true })
function pick(o) { selectedOrderNo.value = o.orderNo; otpInput.value = ''; commentText.value = ''; rejectReason.value = '' }

// The order's own currentStage drives which panel renders — once it's moved
// past the gate, we show what happened instead of the action controls, and
// keep showing it until the user picks a different order.
const otpOutcome = computed(() => {
  if (!order.value || order.value.currentStage === STAGE.OTP_CONFIRMATION) return null
  if (order.value.currentStage === LATERAL_STATES.CANCELLED && order.value.cancelledBy === 'pharmacy') {
    return { type: 'declined', text: order.value.cancelReason || 'Declined by pharmacy.' }
  }
  return { type: 'confirmed', text: 'Confirmed by the pharmacy — sent to the Sales Supervisor for approval.' }
})
const supervisorOutcome = computed(() => {
  if (!order.value || order.value.currentStage === STAGE.SUPERVISOR_APPROVAL) return null
  if (order.value.currentStage === LATERAL_STATES.CANCELLED && order.value.cancelledBy === 'supervisor') {
    return { type: 'rejected', text: order.value.cancelReason || 'Rejected by the supervisor.' }
  }
  if (order.value.currentStage === LATERAL_STATES.RETURNED) {
    const lastComment = (order.value.comments || [])[order.value.comments.length - 1]
    return { type: 'commented', text: lastComment ? lastComment.text : 'Returned to the MR with a comment.' }
  }
  return { type: 'approved', text: 'Approved — released to the Telecaller for validation.' }
})

const approverName = computed(() => 'P. Moyo') // supervisor, for the second gate only

function confirmOtp() {
  if (!order.value) return
  const res = store.salesExecV2.confirmOtp(order.value.orderNo, otpInput.value)
  if (!res.ok) { notify(res.reason === 'wrong-code' ? 'Incorrect OTP' : 'Not awaiting OTP'); return }
  notify('Confirmed — sent to Sales Supervisor')
  otpInput.value = ''
  // Deliberately NOT clearing selectedOrderNo — stays on this order so the
  // success state (rendered below, driven by the order's new stage) shows
  // instead of jumping to the next queued order or the empty state.
}
function declineOtp() {
  if (!order.value) return
  store.salesExecV2.declineOtp(order.value.orderNo, rejectReason.value.trim())
  notify('Declined by pharmacy')
  rejectReason.value = ''
}
function resend() {
  if (!order.value) return
  const code = store.salesExecV2.resendOtp(order.value.orderNo)
  if (code) notify(`New OTP code generated`)
}
// The supervisor gate acts on the MR's own order records, so approving here
// advances the same object the MR is watching on the left-hand phone.
function approve() {
  if (!order.value) return
  const o = order.value
  o.statusHistory.push({ stage: STAGE.TELECALLER_VALIDATION, timestamp: new Date().toISOString() })
  o.currentStage = STAGE.TELECALLER_VALIDATION
  o.approvedBy = approverName.value
  notify('Approved — released to Telecaller')
}
function reject() {
  if (!order.value) return
  const o = order.value
  o.currentStage = LATERAL_STATES.CANCELLED
  o.cancelledBy = 'supervisor'
  o.cancelReason = rejectReason.value.trim() || 'Rejected by supervisor'
  notify('Rejected')
  rejectReason.value = ''
}
function comment() {
  if (!order.value || !commentText.value.trim()) return
  const o = order.value
  o.comments = o.comments || []
  o.comments.push({ from: `Sales Supervisor — ${approverName.value}`, text: commentText.value.trim(), timestamp: new Date().toISOString() })
  o.currentStage = LATERAL_STATES.RETURNED
  notify('Returned to MR for modification')
  commentText.value = ''
}
</script>

<template>
  <div>
    <div class="card module-header" style="display:flex;justify-content:space-between;align-items:flex-end;gap:12px;flex-wrap:wrap;">
      <h2>Order Approval Flow</h2>
      <div v-if="!isSupervisor" style="display:flex;gap:4px;">
        <button class="filter-select" :style="gate==='otp'?'background:var(--color-primary-dark);color:#fff;border-color:var(--color-primary-dark);':'cursor:pointer;'"
                @click="gate='otp'; selectedOrderNo=null">Pharmacy OTP ({{ otpQueue.length }})</button>
        <button class="filter-select" :style="gate==='supervisor'?'background:var(--color-primary-dark);color:#fff;border-color:var(--color-primary-dark);':'cursor:pointer;'"
                @click="gate='supervisor'; selectedOrderNo=null">Supervisor Approval ({{ approvalQueue.length }})</button>
      </div>
      <div v-else class="eyebrow">Supervisor Approval ({{ approvalQueue.length }})</div>
    </div>

    <div v-if="flash" style="position:fixed;bottom:24px;right:24px;background:var(--color-primary-dark);color:#fff;padding:10px 16px;border-radius:8px;font-size:13px;box-shadow:var(--shadow-lg);z-index:120;">{{ flash }}</div>

    <div v-if="queue.length > 1" class="card" style="padding:12px 16px;margin-bottom:16px;">
      <div style="display:flex;gap:6px;flex-wrap:wrap;">
        <button v-for="o in queue" :key="o.orderNo" class="filter-select"
                :style="order && order.orderNo===o.orderNo ? 'background:var(--color-accent);color:#fff;border-color:var(--color-accent);' : 'cursor:pointer;'"
                @click="pick(o)">{{ o.orderNo }}</button>
      </div>
    </div>

    <div v-if="!order" class="card empty-state" style="padding:40px;">
      <h3>Nothing awaiting {{ gate==='otp' ? 'pharmacy confirmation' : 'supervisor approval' }}</h3>
    </div>

    <!-- OTP gate: ONE device. The MR is physically at the pharmacy — there's
         no separate pharmacist app/login, they read the code off the
         pharmacist's phone (or are told it) and enter it themselves. -->
    <div v-else-if="gate==='otp'" style="display:flex;justify-content:center;">
      <div>
        <div style="text-align:center;margin-bottom:10px;">
          <div class="eyebrow">Medical Rep's device — on-site at the pharmacy</div>
          <strong style="font-size:14px;">Tendai Moyo</strong>
        </div>
        <div style="width:320px;border:10px solid #1d1d1f;border-radius:34px;background:var(--color-surface);overflow:hidden;box-shadow:var(--shadow-lg);">
          <div style="background:#1d1d1f;height:22px;"></div>
          <div style="padding:16px;min-height:480px;display:flex;flex-direction:column;">
            <div class="eyebrow" style="margin-bottom:4px;">{{ order.orderNo }}</div>
            <h3 style="font-size:15px;margin:0 0 2px;">{{ order.pharmacyName }}</h3>
            <div class="cell-muted" style="font-size:11.5px;margin-bottom:12px;">{{ order.orderDate }}</div>

            <table class="data-table" style="font-size:11px;margin-bottom:12px;">
              <thead><tr><th>Product</th><th>Qty</th><th>Free</th></tr></thead>
              <tbody>
                <tr v-for="li in order.lineItems" :key="li.productSku">
                  <td class="cell-strong" style="font-size:11px;">{{ li.productName }}</td>
                  <td class="cell-muted">{{ li.quantity }}</td>
                  <td class="cell-muted">{{ li.freeQuantity || 0 }}</td>
                </tr>
              </tbody>
            </table>
            <div class="oos-remarks" style="margin-bottom:14px;">
              <strong style="display:block;font-size:10px;text-transform:uppercase;color:var(--color-text-muted);">Order Value</strong>
              ${{ order.orderValue.toLocaleString() }}
            </div>

            <template v-if="!otpOutcome">
              <div class="eyebrow" style="margin-bottom:8px;">Confirm with the pharmacist</div>
              <p class="cell-muted" style="font-size:11.5px;margin:0 0 12px;">
                An OTP was generated for this order — check it with the pharmacist and enter it below.
              </p>
              <div class="oos-remarks" style="margin-bottom:14px;text-align:center;">
                <strong style="display:block;font-size:10px;text-transform:uppercase;color:var(--color-text-muted);">Generated OTP</strong>
                <span style="font-family:var(--font-display);font-size:24px;letter-spacing:5px;">{{ order.otpCode || '••••' }}</span>
              </div>
              <div class="form-group">
                <label>Enter OTP</label>
                <input class="form-input" maxlength="4" v-model="otpInput" placeholder="4-digit code" />
              </div>
              <button class="btn-primary" :disabled="otpInput.trim().length !== 4" @click="confirmOtp">Confirm Order</button>
              <button class="filter-select" style="cursor:pointer;margin-top:8px;" @click="resend">Regenerate OTP</button>

              <div style="margin-top:auto;padding-top:12px;">
                <input class="form-input" v-model="rejectReason" placeholder="Reason (optional)" style="margin-bottom:8px;" />
                <button class="filter-select" style="cursor:pointer;color:var(--status-red);width:100%;" @click="declineOtp">Pharmacy Declined the Order</button>
              </div>
            </template>

            <!-- Persistent outcome — stays until a different order is picked -->
            <template v-else>
              <div class="oos-remarks" :style="otpOutcome.type==='declined' ? 'border-color:var(--status-red);' : 'border-color:var(--status-green);'">
                <strong style="display:block;font-size:11px;text-transform:uppercase;margin-bottom:4px;" :style="otpOutcome.type==='declined' ? 'color:var(--status-red);' : 'color:var(--status-green);'">
                  {{ otpOutcome.type==='declined' ? '✗ Declined' : '✓ Confirmed' }}
                </strong>
                {{ otpOutcome.text }}
              </div>
              <div style="margin-top:auto;padding-top:12px;">
                <span class="cell-muted" style="font-size:11.5px;">Pick another order above to continue, or switch gates.</span>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Supervisor gate: two genuinely separate people/devices -->
    <div v-else style="display:flex;gap:28px;flex-wrap:wrap;justify-content:center;align-items:flex-start;">
      <div>
        <div style="text-align:center;margin-bottom:10px;">
          <div class="eyebrow">Raised by</div>
          <strong style="font-size:14px;">Medical Rep — Tendai Moyo</strong>
        </div>
        <div style="width:300px;border:10px solid #1d1d1f;border-radius:34px;background:var(--color-surface);overflow:hidden;box-shadow:var(--shadow-lg);">
          <div style="background:#1d1d1f;height:22px;"></div>
          <div style="padding:16px;min-height:440px;display:flex;flex-direction:column;">
            <div class="eyebrow" style="margin-bottom:4px;">{{ order.orderNo }}</div>
            <h3 style="font-size:15px;margin:0 0 2px;">{{ order.pharmacyName }}</h3>
            <div class="cell-muted" style="font-size:11.5px;margin-bottom:12px;">{{ order.orderDate }}</div>

            <table class="data-table" style="font-size:11px;margin-bottom:12px;">
              <thead><tr><th>Product</th><th>Qty</th><th>Free</th></tr></thead>
              <tbody>
                <tr v-for="li in order.lineItems" :key="li.productSku">
                  <td class="cell-strong" style="font-size:11px;">{{ li.productName }}</td>
                  <td class="cell-muted">{{ li.quantity }}</td>
                  <td class="cell-muted">{{ li.freeQuantity || 0 }}</td>
                </tr>
              </tbody>
            </table>
            <div class="oos-remarks" style="margin-bottom:12px;">
              <strong style="display:block;font-size:10px;text-transform:uppercase;color:var(--color-text-muted);">Order Value</strong>
              ${{ order.orderValue.toLocaleString() }}
            </div>

            <div style="margin-top:auto;">
              <div class="eyebrow" style="margin-bottom:6px;">Status</div>
              <span class="status-pill yellow"><span class="dot"></span>{{ order.currentStage }}</span>
              <div class="cell-muted" style="font-size:11px;margin-top:8px;">
                <template v-if="!supervisorOutcome">Confirmed by pharmacy. Waiting for supervisor approval.</template>
                <template v-else>{{ supervisorOutcome.text }}</template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style="align-self:center;text-align:center;padding-top:40px;">
        <div style="font-size:26px;color:var(--color-text-muted);">→</div>
        <div class="cell-muted" style="font-size:11px;max-width:90px;margin-top:4px;">awaiting approval</div>
      </div>

      <div>
        <div style="text-align:center;margin-bottom:10px;">
          <div class="eyebrow">Approved by</div>
          <strong style="font-size:14px;">Sales Supervisor — {{ approverName }}</strong>
        </div>
        <div style="width:300px;border:10px solid #1d1d1f;border-radius:34px;background:var(--color-surface);overflow:hidden;box-shadow:var(--shadow-lg);">
          <div style="background:#1d1d1f;height:22px;"></div>
          <div style="padding:16px;min-height:440px;display:flex;flex-direction:column;">
            <template v-if="!supervisorOutcome">
              <div class="eyebrow" style="margin-bottom:8px;">Approve order</div>
              <p class="cell-muted" style="font-size:11.5px;margin:0 0 4px;">Raised by Tendai Moyo</p>
              <p class="cell-muted" style="font-size:11.5px;margin:0 0 12px;">
                Pharmacy confirmed via OTP · ${{ order.orderValue.toLocaleString() }}
              </p>
              <table class="data-table" style="font-size:11px;margin-bottom:14px;">
                <thead><tr><th>Product</th><th>Qty</th><th>Value</th></tr></thead>
                <tbody>
                  <tr v-for="li in order.lineItems" :key="li.productSku">
                    <td class="cell-strong" style="font-size:11px;">{{ li.productName }}</td>
                    <td class="cell-muted">{{ li.quantity }}</td>
                    <td class="cell-muted">${{ li.lineValue.toLocaleString() }}</td>
                  </tr>
                </tbody>
              </table>
              <button class="btn-primary" @click="approve">Approve</button>
              <div style="margin-top:10px;">
                <input class="form-input" v-model="commentText" placeholder="Comment back to MR…" style="margin-bottom:8px;" />
                <button class="filter-select" style="cursor:pointer;width:100%;" :disabled="!commentText.trim()" @click="comment">Comment &amp; Return</button>
              </div>
              <div style="margin-top:auto;padding-top:12px;">
                <input class="form-input" v-model="rejectReason" placeholder="Rejection reason" style="margin-bottom:8px;" />
                <button class="filter-select" style="cursor:pointer;color:var(--status-red);width:100%;" @click="reject">Reject</button>
              </div>
            </template>

            <!-- Persistent outcome — stays until a different order is picked -->
            <template v-else>
              <div class="oos-remarks" :style="{
                approved: 'border-color:var(--status-green);',
                rejected: 'border-color:var(--status-red);',
                commented: 'border-color:var(--color-accent);',
              }[supervisorOutcome.type]">
                <strong style="display:block;font-size:11px;text-transform:uppercase;margin-bottom:4px;" :style="{
                  approved: 'color:var(--status-green);',
                  rejected: 'color:var(--status-red);',
                  commented: 'color:var(--color-accent);',
                }[supervisorOutcome.type]">
                  {{ { approved: '✓ Approved', rejected: '✗ Rejected', commented: '↩ Commented & Returned' }[supervisorOutcome.type] }}
                </strong>
                {{ supervisorOutcome.text }}
              </div>
              <div style="margin-top:auto;padding-top:12px;">
                <span class="cell-muted" style="font-size:11.5px;">Pick another order above to continue, or switch gates.</span>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
